import { DOMAIN_LABELS, formatScore } from "../../../pearliCore/assessment";
import React, { useRef, useState } from "react";
import { Header } from "../Header-Assesment/Header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretUp,
  faCaretDown,
  faRotateRight,
  faCircleInfo,
  faFileAudio,
} from "@fortawesome/free-solid-svg-icons";
import { Chart } from "../Graphing/Chart";
import { useNavigate } from "react-router-dom";
import SealCheck from "../../../assets/SealCheck.svg";
import { headerConfig } from "../../../axiosconfig.js";
import axios from "axios";
import { useCookies } from "react-cookie";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Markdown from "react-markdown";
import { toast } from "react-toastify";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import recordingUrl from "../../../pearliCore/PEARLI.m4a";
import "./AssessmentTemplateAudio.css";
import { arraySum } from "../../../utils/helper.js";

const API_URL = process.env.REACT_APP_API_URL;

// Tooltip style
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 450,
    fontFamily: "Roboto",
    fontSize: theme.typography.pxToRem(18),
    border: "1px solid #dadde9",
  },
}));

export const AssessmentTemplate = ({
  headerTitle,
  headerSubtitle,
  breadcrumbs,
  tags,
  title,
  subtitle,
  scores,
  clickButton,
  summaries,
  overAll,
  reportKey,
}) => {
  const navigate = useNavigate();
  const reportView = useRef();
  const [cookies, setCookie] = useCookies(["key"]);
  const [showPlayer, setShowPlayer] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);


  // --- Email sending & persistence ---
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(
    () => sessionStorage.getItem(`pearliEmailSent:${reportKey}`) === "true"
  );

  const isTouchDevice = () =>
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const avg =
    Array.isArray(scores) && scores.length === 6
      ? (arraySum(scores) / scores.length).toFixed(1)
      : "0.0";

  // --- Email sending logic ---
  const handleEmailSend = async () => {
    if (!reportKey || emailSent || isSendingEmail) return;

    setIsSendingEmail(true);
    try {
      const sendResponse = await axios.post(
        `${API_URL}/selfAssesments`,
        {
          request: "sendEmail",
          key: cookies["key"],
          reportKey,
        },
        headerConfig
      );

      if (sendResponse?.data?.result !== 1) {
        throw new Error(sendResponse?.data?.message || "Email send failed");
      }

      toast.success("The report was sent to your email! Thanks for taking the assessment.");
      setEmailSent(true);
      sessionStorage.setItem(`pearliEmailSent:${reportKey}`, "true"); // persist across page refresh
    } catch (err) {
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // --- Retake navigation ---
  const handleRetake = async () => {
    try {
      const result = await axios.post(`${API_URL}/session`, { request: "startSession" }, headerConfig);
      setCookie("key", result.data.key, { path: "/", sameSite: "lax" });
      navigate("/Assessment/maincontent/physical");
    } catch {
      toast.error("Could not start a new assessment. Please retry.");
    }
  };

  return (
    <div className="mainReport">
      <Header title={headerTitle} subtitle={headerSubtitle} />

      {/* Breadcrumbs */}
      <div className="toppart">
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {crumb.icon && (
                <FontAwesomeIcon icon={crumb.icon} style={{ color: "#c1c0bd" }} />
              )}
              <p className={crumb.active ? "element-text3" : "element-text"}>
                {crumb.text}
              </p>
              {i !== breadcrumbs.length - 1 && <p className="overline">/</p>}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mainpart">
        <div className="assessments-report">
          {/* Tags */}
          <div className="frame-1">
            {tags.map((tag, i) => (
              <p className="bar-tag" key={i}>
                <span className="bar-tag-text">
                  <img src={SealCheck} alt="Done" className="check-icon-inline" />
                  {tag}
                </span>
              </p>
            ))}
          </div>

          {/* Core content */}
          <div className="printView" ref={reportView}>
            <div className="frame-2">
              <p className="title-main">{title}</p>
              <p className="subtitle-main">{subtitle}</p>
            </div>

            {/* Description */}
            <div className="Report-frame-1"></div>

            {/* Chart + Average + Info Tooltip + Audio */}
            <div className="Report-frame-3">
              <div className="assessment-visual-block">
                <div className="assessment-chart-wrapper">
                  <Chart scores={scores} />
                </div>
                <div className="textAverageScore">Average Score: {avg}/5.0</div>

                {/* Info icon */}
                <div className="assessment-info-icon">
                  <HtmlTooltip
                    title={
                      <>
                        Practice PEARLI™ well-being guidance goes over the six
                        domains of life, staying a few moments on each domain
                        to reflect on it in the present moment.
                      </>
                    }
                    placement="bottom"
                    arrow
                    open={isTouchDevice() ? openTooltip : undefined}
                    onClose={() => setOpenTooltip(false)}
                    disableHoverListener={isTouchDevice()}
                    disableTouchListener={!isTouchDevice()}
                  >
                    <button
                      type="button"
                      className="info-button"
                      onClick={() => isTouchDevice() && setOpenTooltip(!openTooltip)}
                    >
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </button>
                  </HtmlTooltip>
                </div>


              </div>

              {/* Scores and summary */}
              <div className="scoreBox" id="scoreView">
                <div className="scoreView">
                  {["P", "E", "A", "R", "L", "I"].map((label, i) => (
                    <React.Fragment key={label}>
                      <button
                        className={`collapsible coll_${Math.round(scores[i])}`}
                        onClick={(e) => {
                          e.currentTarget.classList.toggle("active"); // toggle caret state
                          clickButton(label);
                        }}
                      >
                        {
                          DOMAIN_LABELS[i]
                        }{" "}
                        <span className={`score c${Math.round(scores[i])}`}>
                          {formatScore(scores[i])}&nbsp;
                          <FontAwesomeIcon icon={faCaretDown} />
                          <FontAwesomeIcon className="up" icon={faCaretUp} />
                        </span>
                      </button>
                      <div className="contentReport" id={label}>
                        <Markdown>{String(summaries[i] || "")}</Markdown>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="starDisplay">
                <div className="starBox">
                  <Markdown>{String(overAll || "")}</Markdown>
                </div>
                {/* Audio */}
                <div className="assessment-audio-section">
                  {!showPlayer ? (
                    <button
                      className="assessment-play-button"
                      onClick={() => setShowPlayer(true)}
                    >
                      <FontAwesomeIcon icon={faFileAudio} />
                      &nbsp;Play Mindfulness Guidance
                    </button>
                  ) : (
                    <div className="assessment-audio-player">
                      <div className="assessment-audio-player-inner">
                        <AudioPlayer
                          src={recordingUrl}
                          autoPlay={false}
                          progressJumpSteps={{ backward: 10000, forward: 10000 }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="Report-frame-4">
              <button
                className="email"
                onClick={handleEmailSend}
                disabled={!reportKey || isSendingEmail || emailSent}
                style={{
                  opacity: isSendingEmail || emailSent ? 0.6 : 1,
                  cursor: isSendingEmail || emailSent ? "not-allowed" : "pointer",
                }}
              >
                {isSendingEmail
                  ? "Sending..."
                  : emailSent
                    ? "Email Sent ✓"
                    : "Email My PEARLI™ Report"}
              </button>

              <button
                className="retake"
                onClick={handleRetake}
              >
                <FontAwesomeIcon icon={faRotateRight} /> Retake Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
